/**
 * Correcciones (§4.2) y arrastres (§4.3), como transformaciones puras.
 *
 * §4.2 arranca diciendo que **equivocarse es el caso normal, no la excepción**. De ahí sale
 * la regla que gobierna este archivo: ninguna corrección borra y recrea. Se conserva el `id`
 * y la `idem_key` original, así que corregir dos veces desde dos dispositivos converge al
 * mismo registro en vez de dejar tres filas.
 *
 * Todo lo que necesita un id nuevo o un reloj lo recibe por parámetro: mantiene las
 * funciones puras y hace que los tests no dependan de `Date.now()`.
 */

import type { Fecha, Instante, MotivoOmision, Serie, Sesion, Unidad } from './types.ts';
import type { UltimaMarca } from './kpis/fuerza.ts';

export type GeneradorId = () => string;

// ---------------------------------------------------------------------------
// §4.1 — registro en dos toques
// ---------------------------------------------------------------------------

/**
 * La serie que sigue, idéntica a la anterior. Es el camino de datos del "máximo dos toques
 * para registrar una serie idéntica a la anterior": tocar el ejercicio y tocar repetir.
 *
 * Se copia peso, unidad, reps y máquina de la serie de referencia; lo único que cambia es el
 * número de serie. `rir` NO se copia: era la percepción de esa serie, no de esta.
 */
export function repetirSerie(
  referencia: Serie,
  opciones: {
    sesionId: string;
    numeroSerie: number;
    nuevoId: GeneradorId;
    ahora: Instante;
  },
): Serie {
  return {
    id: opciones.nuevoId(),
    sesion_id: opciones.sesionId,
    ejercicio_id: referencia.ejercicio_id,
    maquina_id: referencia.maquina_id,
    numero_serie: opciones.numeroSerie,
    reps: referencia.reps,
    peso: referencia.peso,
    unidad: referencia.unidad,
    rir: null,
    completada: true,
    motivo_omision: null,
    arrastrada_de_sesion_id: null,
    registrada_en: opciones.ahora,
    idem_key: `${opciones.sesionId}:${referencia.ejercicio_id}:${opciones.numeroSerie}`,
  };
}

/** Primera serie de un ejercicio a partir de su última marca, o desde cero si no hay. */
export function serieDesdeMarca(
  marca: UltimaMarca | null,
  opciones: {
    sesionId: string;
    ejercicioId: string;
    numeroSerie: number;
    nuevoId: GeneradorId;
    ahora: Instante;
    pesoPorDefecto?: number;
    unidadPorDefecto?: Unidad;
    repsPorDefecto?: number;
  },
): Serie {
  if (marca) {
    return repetirSerie(marca.referencia, {
      sesionId: opciones.sesionId,
      numeroSerie: opciones.numeroSerie,
      nuevoId: opciones.nuevoId,
      ahora: opciones.ahora,
    });
  }
  return {
    id: opciones.nuevoId(),
    sesion_id: opciones.sesionId,
    ejercicio_id: opciones.ejercicioId,
    maquina_id: null,
    numero_serie: opciones.numeroSerie,
    reps: opciones.repsPorDefecto ?? 10,
    peso: opciones.pesoPorDefecto ?? 0,
    unidad: opciones.unidadPorDefecto ?? 'lb',
    rir: null,
    completada: true,
    motivo_omision: null,
    arrastrada_de_sesion_id: null,
    registrada_en: opciones.ahora,
    idem_key: `${opciones.sesionId}:${opciones.ejercicioId}:${opciones.numeroSerie}`,
  };
}

// ---------------------------------------------------------------------------
// §4.2 — correcciones
// ---------------------------------------------------------------------------

/** Edita peso y/o reps de una serie ya guardada. Un toque desde el historial (§4.2). */
export function editarSerie(
  serie: Serie,
  cambios: Partial<Pick<Serie, 'reps' | 'peso' | 'unidad' | 'rir'>>,
): Serie {
  return { ...serie, ...cambios };
}

/**
 * Reasigna una serie a otro ejercicio **sin borrarla y recrearla** (§4.2). Es el error más
 * común del registro en vivo: anotar el peso del tríceps en el ejercicio de bíceps.
 *
 * Se conservan `id` e `idem_key`: el registro es el mismo hecho, corregido. Si se generara
 * una fila nueva, el dispositivo que todavía no sincronizó volvería a subir la vieja.
 */
export function reasignarEjercicio(
  serie: Serie,
  nuevoEjercicioId: string,
  nuevaMaquinaId: string | null = null,
): Serie {
  return {
    ...serie,
    ejercicio_id: nuevoEjercicioId,
    // La máquina vieja pertenecía al ejercicio viejo: arrastrarla sería peor que perderla.
    maquina_id: nuevaMaquinaId,
  };
}

/**
 * Mueve una sesión completa de fecha, arrastrando su contenido (§4.2).
 *
 * Las series no se tocan: apuntan a `sesion_id`, así que se mueven solas. Es el motivo por
 * el que la fecha vive en la sesión y no repetida en cada serie — si estuviera repetida,
 * mover un día sería actualizar cuarenta filas y cualquier fallo a mitad dejaría la sesión
 * partida entre dos fechas.
 */
export function moverSesion(sesion: Sesion, nuevaFecha: Fecha): Sesion {
  return { ...sesion, fecha: nuevaFecha };
}

/** Marca un ejercicio como omitido con su motivo (§4.3). */
export function omitirSerie(serie: Serie, motivo: MotivoOmision): Serie {
  return { ...serie, completada: false, motivo_omision: motivo, reps: 0, peso: 0, rir: null };
}

// ---------------------------------------------------------------------------
// §4.3 — arrastre a la siguiente sesión
// ---------------------------------------------------------------------------

/**
 * Crea las series pendientes en la sesión destino a partir de las omitidas en la de origen
 * (§4.3: "ofrecer arrastrarlo automáticamente a la siguiente sesión").
 *
 * Quedan `completada: false` con `motivo_omision: null` — pendientes, no omitidas — y con
 * `arrastrada_de_sesion_id` apuntando al origen, para poder mostrar "viene del viernes".
 * Las omitidas por `decision` o `dolor` no se arrastran solas: insistir automáticamente con
 * algo que se descartó a propósito, o que dolió, sería exactamente lo que no hay que hacer.
 */
export function arrastrarSeries(
  omitidas: Serie[],
  opciones: {
    sesionDestinoId: string;
    nuevoId: GeneradorId;
    ahora: Instante;
    seriesObjetivo?: number;
    motivosArrastrables?: MotivoOmision[];
  },
): Serie[] {
  const motivos = opciones.motivosArrastrables ?? ['equipo_ocupado', 'sin_tiempo'];
  const arrastrables = omitidas.filter(
    (s) => !s.completada && s.motivo_omision !== null && motivos.includes(s.motivo_omision),
  );

  const porEjercicio = new Map<string, Serie>();
  for (const s of arrastrables) if (!porEjercicio.has(s.ejercicio_id)) porEjercicio.set(s.ejercicio_id, s);

  const salida: Serie[] = [];
  for (const [ejercicioId, origen] of porEjercicio) {
    const cuantas = opciones.seriesObjetivo ?? 1;
    for (let n = 1; n <= cuantas; n++) {
      salida.push({
        id: opciones.nuevoId(),
        sesion_id: opciones.sesionDestinoId,
        ejercicio_id: ejercicioId,
        maquina_id: origen.maquina_id,
        numero_serie: n,
        reps: 0,
        peso: 0,
        unidad: origen.unidad,
        rir: null,
        completada: false,
        motivo_omision: null,
        arrastrada_de_sesion_id: origen.sesion_id,
        registrada_en: opciones.ahora,
        idem_key: `${opciones.sesionDestinoId}:${ejercicioId}:${n}`,
      });
    }
  }
  return salida;
}

/** Series pendientes de una sesión: planificadas o arrastradas, todavía sin hacer. */
export function seriesPendientes(series: Serie[], sesionId: string): Serie[] {
  return series.filter(
    (s) => s.sesion_id === sesionId && !s.completada && s.motivo_omision === null,
  );
}

/** Las que vienen arrastradas de otra sesión, para poder decir de dónde. */
export function seriesArrastradas(series: Serie[], sesionId: string): Serie[] {
  return seriesPendientes(series, sesionId).filter((s) => s.arrastrada_de_sesion_id !== null);
}
